"use client";

import { Check, ChevronRight, Shield, Sparkles } from "lucide-react";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { Button } from "@/components/ui/button";
import { PlatformLogo } from "@/components/ui/platform-logo";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import type { PlatformId } from "@/types/platforms";

const CTA_PLATFORMS: PlatformId[] = [
  "youtube",
  "tiktok",
  "instagram",
  "facebook",
  "linkedin",
  "x",
];

export function FinalCTA() {
  const { t } = useJsonTranslation("HomeFinalCta");

  return (
    <div
      data-home-block="final-cta-panel"
      className="relative overflow-hidden px-5 py-10 sm:px-8 sm:py-14"
    >
      {/* Layered luxury background: ambient orb + soft grid */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute top-1/2 left-1/2 h-[520px] w-[980px] max-w-[100vw] -translate-x-1/2 -translate-y-1/2 opacity-35 blur-[160px]"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(52, 211, 153, 0.42) 0%, rgba(255, 122, 24, 0.22) 48%, transparent 72%)",
          }}
          aria-hidden="true"
        />
        <div
          className="hero-grid absolute inset-0 opacity-40"
          aria-hidden="true"
        />
      </div>

      <div className="relative z-10 mx-auto max-w-3xl text-center">
        {/* Badge */}
        <div className="glass text-leaf-600 dark:text-leaf-400 mb-8 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          {t("badge")}
        </div>

        {/* Headline */}
        <h2 className="font-home-display text-foreground mb-6 text-4xl leading-[1.02] font-semibold tracking-normal text-balance md:text-5xl lg:text-6xl">
          <span className="block">{t("titleLine1")}</span>
          <span className="text-gradient-primary mt-1 block">
            {t("titleLine2")}
          </span>
        </h2>

        {/* Subheadline */}
        <p className="text-muted-foreground mx-auto mb-8 max-w-xl text-lg text-pretty sm:text-xl">
          {t("subtitle")}
        </p>

        {/* 6-platform icon row — reinforces scope */}
        <div className="mb-10 flex flex-wrap items-center justify-center gap-2.5">
          {CTA_PLATFORMS.map((p) => (
            <div
              key={p}
              className="glass flex h-10 w-10 items-center justify-center rounded-full transition-transform duration-300 hover:-translate-y-0.5"
              aria-hidden="true"
            >
              <PlatformLogo platform={p} size={20} className="opacity-90" />
            </div>
          ))}
        </div>

        {/* Primary CTA + demoted secondary link */}
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
          <Link href="/signup">
            <Button size="xl" className="group shadow-glow-lg">
              {t("ctaGetStarted")}
              <ChevronRight
                className="ml-1 h-5 w-5 transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Button>
          </Link>
          <Link
            href="/seeds"
            className="text-foreground/70 hover:text-foreground focus-visible:ring-ring inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium underline-offset-4 transition-colors hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            {t("ctaViewPricing")}
          </Link>
        </div>

        {/* Risk-reversal trio — replaces the old single disclaimer line */}
        <div className="mx-auto mt-10 grid max-w-2xl gap-3 sm:grid-cols-3">
          <div className="glass flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm">
            <Check
              className="text-leaf-500 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <span className="text-foreground/80 font-medium">
              {t("risk.noCreditCard")}
            </span>
          </div>
          <div className="glass flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm">
            <Shield
              className="text-leaf-500 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <span className="text-foreground/80 font-medium">
              {t("risk.moneyBack")}
            </span>
          </div>
          <div className="glass flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm">
            <Sparkles
              className="text-leaf-500 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <span className="text-foreground/80 font-medium">
              {t("risk.cancelAnytime")}
            </span>
          </div>
        </div>

        <a
          href="https://x.com/VidSeeds"
          target="_blank"
          rel="noopener noreferrer"
          className="glass text-muted-foreground hover:text-foreground mt-8 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-all duration-200"
        >
          <PlatformLogo platform="x" size={16} />
          {t("followOnX")}
        </a>
      </div>
    </div>
  );
}
